import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchReportInventory, fetchReportInventoryIntelligence } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

export function InventoryReportPage() {
  const f = useReportFilters();

  const qs = useMemo(() => (f.storeId.trim() ? { storeId: f.storeId.trim() } : {}), [f.storeId]);

  const q = useQuery({
    queryKey: ['reports-inventory', qs],
    queryFn: () => fetchReportInventory(qs),
  });

  const intelQ = useQuery({
    queryKey: ['reports-inventory-intelligence', qs],
    queryFn: () => fetchReportInventoryIntelligence(qs),
  });

  const d = q.data;
  const intel = intelQ.data;

  const csv = () => {
    if (!d) return;
    const top = (d.topMovingItems ?? []).map((r) => ({
      kind: 'top_moving',
      name: r.name,
      usedInTickets: r.usedInTickets,
      revenue: r.revenue,
    }));
    const slow = (d.slowMovingItems ?? []).map((r) => ({
      kind: 'slow_moving',
      name: r.name,
      qtyOnHand: r.qtyOnHand,
      daysSinceLastMove: r.daysSinceLastMove,
      value: r.value,
    }));
    exportToCsv([...top, ...slow], 'inventory-report.csv');
  };

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading inventory…</div>;
  if (q.isError || !d) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load inventory report.</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csv}>
          Export CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: 'SKU rows (stock lines)', v: d.totalItems },
          { label: 'Cost basis', v: formatCurrency(d.totalValue) },
          { label: 'Retail value', v: formatCurrency(d.totalRetailValue) },
          { label: 'Low stock rows', v: d.lowStockItems },
          { label: 'Out of stock rows', v: d.outOfStockItems },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium text-slate-500">{k.label}</p>
            <p className="mt-1 text-xl font-bold text-slate-950">{k.v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Top moving (12 mo)</h3>
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {(d.topMovingItems ?? []).map((t) => (
              <li key={t.name} className="flex justify-between py-2">
                <span className="font-medium">{t.name}</span>
                <span className="tabular-nums text-slate-600">
                  ×{t.usedInTickets} · {formatCurrency(t.revenue)}
                </span>
              </li>
            ))}
            {!(d.topMovingItems ?? []).length ? <li className="py-4 text-slate-500">No consumption recorded.</li> : null}
          </ul>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-950">Slow moving (stale qty)</h3>
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {(d.slowMovingItems ?? []).map((t) => (
              <li key={t.name} className="flex justify-between gap-4 py-2">
                <span className="font-medium">{t.name}</span>
                <span className="text-right text-xs text-slate-600">
                  Qty {Number(t.qtyOnHand).toLocaleString(undefined, { maximumFractionDigits: 2 })} · {t.daysSinceLastMove}d idle ·{' '}
                  {formatCurrency(t.value)}
                </span>
              </li>
            ))}
            {!(d.slowMovingItems ?? []).length ? <li className="py-4 text-slate-500">Nothing on hand.</li> : null}
          </ul>
        </div>
      </div>

      {intel?.summary ? (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-950">Dead stock</h3>
            <p className="mt-1 text-xs text-slate-500">
              {intel.summary.deadStockCount} items · {formatCurrency(intel.summary.deadStockValue)} tied up
            </p>
            <ul className="mt-3 max-h-56 divide-y divide-slate-100 overflow-y-auto text-sm">
              {(intel.deadStock ?? []).slice(0, 8).map((item) => (
                <li key={item.itemId} className="flex justify-between py-1.5">
                  <span className="truncate">{item.name}</span>
                  <span className="shrink-0 text-xs text-slate-500">{item.daysSinceLastMove}d</span>
                </li>
              ))}
              {!(intel.deadStock ?? []).length ? <li className="py-3 text-slate-500">No dead stock detected.</li> : null}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-950">Reorder suggestions</h3>
            <p className="mt-1 text-xs text-slate-500">
              {intel.summary.forecastedStockoutCount} items forecasted to stock out soon
            </p>
            <ul className="mt-3 max-h-56 divide-y divide-slate-100 overflow-y-auto text-sm">
              {(intel.reorderSuggestions ?? []).slice(0, 8).map((item) => (
                <li key={item.itemId} className="flex justify-between py-1.5">
                  <span className="truncate">{item.name}</span>
                  <span className="shrink-0 text-xs text-slate-500">
                    {item.daysUntilStockout != null ? `${item.daysUntilStockout}d left` : '—'}
                  </span>
                </li>
              ))}
              {!(intel.reorderSuggestions ?? []).length ? <li className="py-3 text-slate-500">Nothing to reorder.</li> : null}
            </ul>
            <Link to="/reports/parts-forecast" className="mt-3 inline-block text-xs font-semibold text-primary hover:underline">
              View full parts forecast →
            </Link>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-950">Margin by category</h3>
            <ul className="mt-3 divide-y divide-slate-100 text-sm">
              {(intel.marginByCategory ?? []).map((c) => (
                <li key={c.category} className="flex justify-between py-1.5">
                  <span className="capitalize">{c.category.replace(/_/g, ' ')}</span>
                  <span className="font-semibold">{c.marginPct.toFixed(1)}%</span>
                </li>
              ))}
              {!(intel.marginByCategory ?? []).length ? <li className="py-3 text-slate-500">No margin data yet.</li> : null}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
